import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UnsavedChangesDialog } from './_private/UnsavedChangesDialog';

/** The draft the prompt asks about. */
export type DiscardableDraft = {
  title: string;
  /** Where focus goes when the user keeps the draft; see `UnsavedChangesDialog`. */
  focus?(): void;
};

export interface DiscardChangesPrompt {
  /** Opens the prompt; resolves `true` when the user discards the draft. */
  confirmDiscard(draft: DiscardableDraft): Promise<boolean>;
  /** The prompt's dialog; render it once. */
  dialog: ReactNode;
}

/**
 * "Discard unsaved changes?" as a promise, on the shared
 * `UnsavedChangesDialog`: for drafts dropped inside a page (another inline
 * editor opens) rather than by leaving it.
 */
export function useDiscardChangesPrompt(): DiscardChangesPrompt {
  const { t } = useTranslation('patterns');
  // The draft outlives `open`: the closing animation keeps the text and focus
  // returns to it once the dialog is gone.
  const [prompt, setPrompt] = useState<{
    open: boolean;
    draft: DiscardableDraft;
  }>({ open: false, draft: { title: '' } });
  const answerRef = useRef<((discard: boolean) => void) | null>(null);

  const answer = useCallback((discard: boolean) => {
    const resolve = answerRef.current;
    answerRef.current = null;
    setPrompt(current => ({ ...current, open: false }));
    resolve?.(discard);
  }, []);

  const confirmDiscard = useCallback(
    (draft: DiscardableDraft) =>
      new Promise<boolean>(resolve => {
        answerRef.current?.(false);
        answerRef.current = resolve;
        setPrompt({ open: true, draft });
      }),
    []
  );

  // Nobody keeps waiting for a prompt that is gone.
  useEffect(() => () => answer(false), [answer]);

  return {
    confirmDiscard,
    dialog: (
      <UnsavedChangesDialog
        open={prompt.open}
        onOpenChange={open => {
          if (!open) answer(false);
        }}
        onConfirm={() => answer(true)}
        description={t('UnsavedChangesDialog.draftDescription', {
          title: prompt.draft.title,
          interpolation: { escapeValue: false }
        })}
        returnFocus={prompt.draft.focus}
      />
    )
  };
}
